import { useState } from "react";
import { HiDocument, HiX } from "react-icons/hi";
import { FaLinkedin } from "react-icons/fa";

const WritingAndBlogs = () => {
  const [showFullArticle, setShowFullArticle] = useState(false);

  const openArticle = () => {
    setShowFullArticle(true);
    document.body.style.overflow = "hidden";
  };

  const closeArticle = () => {
    setShowFullArticle(false);
    document.body.style.overflow = "unset";
  };

  const renderMarkdownContent = (content) => {
    return content.split("\n\n").map((paragraph, idx) => {
      if (paragraph.startsWith("**") && paragraph.endsWith("**")) {
        return (
          <div
            key={idx}
            className="bg-cyan-600 bg-opacity-10 border-l-4 border-cyan-500 pl-4 py-2 my-6"
          >
            <h4 className="text-xl font-bold text-cyan-400">
              {paragraph.replace(/\*\*/g, "")}
            </h4>
          </div>
        );
      }
      if (paragraph.includes("•")) {
        const lines = paragraph.split("\n");
        const title = lines[0];
        const bullets = lines.slice(1);
        return (
          <div key={idx} className="mb-6">
            <p className="text-[#8892b0] leading-relaxed mb-3">{title}</p>
            <ul className="space-y-2">
              {bullets.map((bullet, bulletIdx) => (
                <li key={bulletIdx} className="flex items-start gap-3">
                  <span className="text-cyan-500 mt-1">•</span>
                  <span className="text-[#8892b0]">
                    {bullet.replace("• ", "")}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        );
      }
      return (
        <p key={idx} className="text-[#8892b0] leading-relaxed mb-6 text-lg">
          {paragraph}
        </p>
      );
    });
  };

  const articleContent = `One year at E-LMIS. Time moves fast when every month feels like a stress test. Looking back, this year feels more like a compressed decade of engineering lessons.

I joined with a strong UI and frontend focus, but my early weeks in technical support and QA rewired how I think. Breaking things in production teaches you how systems actually behave, not how they are supposed to behave in Figma.

**Internal Tooling**
Built a super-admin platform for the Expatriate Work Permit (EWP-v1) system to handle messy operational edge cases. This pushed me past UI polish into workflow design, permissions, data integrity, and real-world failure scenarios.

**Backend Initiative**
Identified gaps in storage handling and built a middleware utility that hashes documents to prevent duplicate uploads. That initiative became my entry point into deeper backend ownership and system-level architecture.

**Full-Stack Pivot**
Assigned to the backend team for an e-commerce platform, I untangled an aging codebase, shipped a new delivery module, and documented APIs so frontend and mobile teams could move fast with zero guesswork.

**EWP-v1: Legacy Under Fire**
I took over major parts of EWP-v1 after senior devs departed. This was more than a legacy system. It was a total mess that required a full-scale archaeological dig to stabilize.

The reality of the codebase:
• Zero documentation and almost no meaningful comments
• Single files running into the thousands of lines
• Rigid component design with no separation of concerns
• A fragile system where every change felt like walking through a minefield

Under heavy production load, I kept the system alive, resolved long-standing concurrency issues, and worked in parallel on the clean EWP-v2 architecture. As a result, support tickets for recurring issues dropped from nearly every day to just a couple every few weeks. Later, during a stakeholder meeting, the director highlighted EWP-v1 as one of the top 3 performing government platforms.

**Commission Enforcement Bank Portal**
I engineered this system properly from the ground up. I focused on deterministic state handling, multi-layer submission locks, and performance-first thinking. This was a financial system where mistakes cost time and trust, so I made it as secure and performance-conscious as possible. It has been in production for months with zero critical issues and remains one of the few systems the QA team explicitly praised for its stability.

**Integration Work & Becoming "The Go-To"**
I handled multiple system integrations with government institutions, including the Ministry of Revenue, Ministry of Trade, and the National ID system. I resolved long-standing API issues through close coordination with internal teams and centers. Eventually, other senior developers began referencing and reusing my integration patterns for their own implementations.

Somewhere in that process, I became the person people call when a system is on fire and production needs an urgent fix.

**Role Transition & Growth**
Over the year, I went from a frontend-heavy developer to a senior full-stack role and eventually into a lead developer position. Today, my focus is on architecture, system integration, API contracts, and keeping complex systems stable at scale.

It was a heavy year with no slow months, but I'm proud of the fact that I consistently leave systems better than I found them.

I am looking forward to year two. More leadership, cleaner architecture, and fewer fires. When systems do catch fire, I am more than capable of putting them out.`;

  return (
    <div name="journal" className="w-full bg-[#0a192f] text-gray-300 py-20">
      <div className="max-w-[1200px] mx-auto px-8">
        <div className="pb-12">
          <h2 className="text-4xl sm:text-5xl font-bold inline-block border-b-4 border-cyan-600">
            Writing & Blogs
          </h2>
          <p className="text-[#8892b0] text-lg mt-4">
            Thoughts on technical growth, system architecture, and engineering
            leadership
          </p>
        </div>

        <div className="bg-[#112240] rounded-xl border border-cyan-600 border-opacity-20 overflow-hidden">
          <div className="grid lg:grid-cols-2 gap-8 p-8 lg:p-12">
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-4">
                <HiDocument className="text-3xl text-cyan-500" />
                <span className="px-3 py-1 bg-cyan-600 bg-opacity-20 text-cyan-400 rounded-lg text-sm font-semibold">
                  Featured Article
                </span>
              </div>

              <h3 className="text-3xl sm:text-4xl font-bold text-white leading-tight">
                From Frontend to Systems Architecture: A One-Year Retrospective
              </h3>

              <p className="text-cyan-500 text-lg font-medium">
                Lessons learned from a year of firefighting, legacy digs, and
                lead-level growth at E-LMIS
              </p>

              <div className="bg-[#0a192f] border border-cyan-600 border-opacity-30 rounded-lg p-6">
                <p className="text-[#8892b0] text-lg leading-relaxed italic">
                  "Breaking things in production teaches you how systems
                  actually behave, not how they are supposed to behave in Figma.
                  This year wasn't just about shipping features. It was about
                  untangling thousand-line files, designing API contracts for
                  national institutions, and learning that stability is the
                  highest form of engineering."
                </p>
              </div>

              <div className="space-y-3">
                <h4 className="text-xl font-bold text-white">
                  Key Topics Covered:
                </h4>
                <div className="space-y-2">
                  {[
                    "Archaeological engineering on legacy systems",
                    "Building high-stakes financial infrastructure",
                    "Government-level API integrations",
                    "Transition from frontend to technical leadership",
                    "Crisis management and system stabilization",
                  ].map((topic, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <span className="text-cyan-500 mt-1">▹</span>
                      <p className="text-[#8892b0]">{topic}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 pt-6">
                <button
                  onClick={openArticle}
                  className="flex items-center justify-center gap-3 px-6 py-3 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-all duration-300 font-semibold"
                >
                  <HiDocument className="text-xl" />
                  Read Full Article
                </button>

                <a
                  href="https://www.linkedin.com/pulse/from-frontend-systems-architecture-one-year-e-lmis-beki-d-abate-kmxbf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-3 px-6 py-3 border-2 border-cyan-600 text-cyan-400 rounded-lg hover:bg-cyan-600 hover:text-white transition-all duration-300 font-semibold"
                >
                  <FaLinkedin className="text-xl" />
                  View on LinkedIn
                </a>
              </div>
            </div>

            <div className="flex items-center justify-center">
              <div className="bg-[#0a192f] rounded-lg p-8 border border-cyan-600 border-opacity-30 w-full max-w-md">
                <div className="text-center space-y-4">
                  <div className="w-16 h-16 bg-cyan-600 bg-opacity-20 rounded-full flex items-center justify-center mx-auto">
                    <HiDocument className="text-3xl text-cyan-500" />
                  </div>

                  <div className="space-y-2">
                    <h4 className="text-xl font-bold text-white">
                      Article Stats
                    </h4>
                    <div className="grid grid-cols-2 gap-4 text-center">
                      <div>
                        <div className="text-2xl font-bold text-cyan-500">
                          12
                        </div>
                        <div className="text-[#8892b0] text-sm">
                          Months Covered
                        </div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-cyan-500">
                          5+
                        </div>
                        <div className="text-[#8892b0] text-sm">
                          Major Projects
                        </div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-cyan-500">
                          3
                        </div>
                        <div className="text-[#8892b0] text-sm">
                          Gov Integrations
                        </div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-cyan-500">
                          70%
                        </div>
                        <div className="text-[#8892b0] text-sm">
                          Issue Reduction
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Full Article Modal */}
        {showFullArticle && (
          <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4">
            <div className="bg-[#112240] rounded-xl border border-cyan-600 border-opacity-30 max-w-4xl max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-[#112240] border-b border-cyan-600 border-opacity-20 p-6 flex justify-between items-center">
                <div className="flex items-center gap-4">
                  <HiDocument className="text-3xl text-cyan-500" />
                  <div>
                    <h3 className="text-2xl font-bold text-white">
                      From Frontend to Systems Architecture
                    </h3>
                    <p className="text-cyan-500">One Year at E-LMIS</p>
                  </div>
                </div>
                <button
                  onClick={closeArticle}
                  className="text-[#8892b0] hover:text-white transition-colors duration-300"
                >
                  <HiX className="text-3xl" />
                </button>
              </div>

              <div className="p-8">
                <div className="prose prose-invert max-w-none">
                  {renderMarkdownContent(articleContent)}
                </div>

                <div className="mt-12 pt-8 border-t border-cyan-600 border-opacity-20">
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <a
                      href="https://www.linkedin.com/pulse/from-frontend-systems-architecture-one-year-e-lmis-beki-d-abate-kmxbf"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-3 px-6 py-3 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-all duration-300 font-semibold"
                    >
                      <FaLinkedin className="text-xl" />
                      View Original on LinkedIn
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WritingAndBlogs;
